import React, { useEffect, useMemo, useState, useRef } from "react";
import { Card, Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import "./PacksByICBComponent.css";
import { createPortal } from "react-dom";

const API_URL = process.env.REACT_APP_API_URL;

const PortalTooltip = ({ active, payload, coordinate, containerRef }) => {
  if (!active || !payload?.length || !coordinate || !containerRef?.current) return null;

  const row = payload[0]?.payload;

  // Convert Recharts chart coordinates -> page coordinates
  const rect = containerRef.current.getBoundingClientRect();
  const left = rect.left + window.scrollX + coordinate.x + 12;
  const top = rect.top + window.scrollY + coordinate.y - 10;

  const last12 = row?.packs_last_12m ?? 0;
  const prev12 = row?.packs_prev_12m ?? 0;

  const node = (
    <div
      style={{
        position: "absolute",
        left,
        top,
        zIndex: 999999,
        pointerEvents: "none",
        background: "#fff",
        borderRadius: 10,
        border: "1px solid rgba(10,37,64,0.12)",
        boxShadow: "0 8px 18px rgba(10,37,64,0.12)",
        padding: "10px 12px",
        maxWidth: 280,
        color: "#0A2540",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13, lineHeight: "16px" }}>
        {row?.icb_name}
      </div>

      <div style={{ marginTop: 8, fontSize: 13, display: "grid", gap: 6 }}>
        <div>
          <span style={{ fontWeight: 700 }}>Last 12M:</span>{" "}
          {Intl.NumberFormat().format(last12)}
        </div>
        <div>
          <span style={{ fontWeight: 700 }}>Previous 12M:</span>{" "}
          {Intl.NumberFormat().format(prev12)}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

const truncateName = (text, max = 24) => {
  if (!text) return "";
  return text.length > max ? text.substring(0, max) + "…" : text;
};

const PacksByICBComponent = ({
  product,
  title,
  subtitle,
  apiEndpoint,
  maxHeight = 520,
  onICBSelect,
  selectedICB
}) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const chartContainerRef = useRef(null);

  // two distinct gradients (Deep Blue and Teal)
  const gradLastId = useMemo(() => `gradLast-${Math.random().toString(36).slice(2)}`, []);
  const gradPrevId = useMemo(() => `gradPrev-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${API_URL}${apiEndpoint}?product=${product}`);
        if (!res.ok) throw new Error(`Request failed (${res.status})`);

        const json = await res.json();
        if (!alive) return;

        setRawData(Array.isArray(json) ? json : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load data");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      alive = false;
    };
  }, [apiEndpoint, product]);

  const data = useMemo(() => {
    return [...rawData]
      .map((r) => ({
        icb_name: String(r.icb_name || ""),
        packs_last_12m: Number(r.packs_last_12m || 0),
        packs_prev_12m: Number(r.packs_prev_12m || 0),
        // sort key: last 12 months primarily
        sort_packs: Number(r.packs_last_12m || 0),
      }))
      .sort((a, b) => (b.sort_packs || 0) - (a.sort_packs || 0));
  }, [rawData]);

  const ROW_H = 38;

  // With 2 bars per category, keep bars a bit thinner
  const BAR_SIZE = 12;

  const CHART_MARGIN_TOP = 8;
  const CHART_MARGIN_BOTTOM = 8;
  const X_AXIS_HEIGHT = 32;

  const chartHeight =
    data.length * ROW_H + CHART_MARGIN_TOP + CHART_MARGIN_BOTTOM + X_AXIS_HEIGHT;

  if (error) {
    return (
      <Card className="packsCard">
        <Box className="packsHeader">
          <Typography variant="h6" className="packsTitle">
            {title}
          </Typography>
          <div className="packsSub">{subtitle || ""}</div>
        </Box>
        <div className="packsError">{error}</div>
      </Card>
    );
  }

  return (
    <Card className="packsCard">
      <Box className="packsHeader">
        <Typography variant="h6" className="packsTitle">
          {title}
        </Typography>
        <div className="packsSub">
            {loading ? (
                "Loading…"
            ) : (
                <>
                {data.length} ICBs • {subtitle || ""}
                </>
            )}
        </div>
      </Box>

      <Box className="packsBody">
        <div className="packsScroll" style={{ maxHeight }}>
          <div className="packsGrid" style={{ "--row-h": `${ROW_H}px` }}>
            {/* Left labels */}
            <div
              className="packsLabels"
              style={{
                paddingTop: CHART_MARGIN_TOP,
                paddingBottom: CHART_MARGIN_BOTTOM + X_AXIS_HEIGHT,
              }}
            >
              {(loading ? Array.from({ length: 10 }) : data).map((row, idx) => {
                const name = loading ? "Loading…" : row.icb_name;
                const displayName = truncateName(name, 24);
                const isSelected = !loading && selectedICB === row.icb_name;
                
                return (
                  <div
                    key={loading ? idx : name}
                    className="packsLabelRow"
                    title={name}
                    onClick={() => !loading && onICBSelect?.(row.icb_name)}
                    style={{
                      cursor: loading ? "default" : "pointer",
                      fontWeight: isSelected ? 700 : 400,
                      background: isSelected ? "rgba(0,191,165,0.12)" : "transparent",
                      borderRadius: 8,
                      padding: "0 6px"
                    }}
                  >
                    <div className="packsLabelText">{displayName}</div>
                  </div>
                );
              })}
            </div>

            {/* Chart */}
            <div
              className="packsChartWrap"
              style={{ height: chartHeight, position: "relative" }}
              ref={chartContainerRef}
            >
              {!loading && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 8, right: 18, left: 0, bottom: 8 }}
                    barCategoryGap={Math.max(10, ROW_H - 2 * BAR_SIZE - 6)}
                    barGap={6}
                  >
                    <defs>
                      {/* Last 12M: Deep Blue -> slightly lighter */}
                      <linearGradient id={gradLastId} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#0A2540" />
                        <stop offset="100%" stopColor="rgba(10,37,64,0.55)" />
                      </linearGradient>

                      {/* Previous 12M: Teal -> slightly lighter */}
                      <linearGradient id={gradPrevId} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#00BFA5" />
                        <stop offset="100%" stopColor="rgba(0,191,165,0.45)" />
                      </linearGradient>
                    </defs>

                    <YAxis type="category" dataKey="icb_name" hide={true} />

                    <XAxis
                      type="number"
                      height={X_AXIS_HEIGHT}
                      tick={{ fontSize: 12, fill: "#0A2540" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => Intl.NumberFormat().format(v)}
                    />

                    <Tooltip
                      cursor={{ fill: "rgba(0,0,0,0.04)" }}
                      content={<PortalTooltip containerRef={chartContainerRef} />}
                    />

                    <Bar
                      name="Last 12 Months"
                      dataKey="packs_last_12m"
                      fill={`url(#${gradLastId})`}
                      radius={[0, 10, 10, 0]}
                      barSize={BAR_SIZE}
                      background={{
                        fill: "rgba(10,37,64,0.06)",
                        radius: 10,
                      }}
                      isAnimationActive={true}
                    />

                    <Bar
                      name="Previous 12 Months"
                      dataKey="packs_prev_12m"
                      fill={`url(#${gradPrevId})`}
                      radius={[0, 10, 10, 0]}
                      barSize={BAR_SIZE}
                      isAnimationActive={true}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </Box>
    </Card>
  );
};

export default PacksByICBComponent;